import React from 'react'
import { PencilSimpleLine, Power, Prohibit } from '@phosphor-icons/react'
import { Box, Stack, Typography } from '@mui/material'
import FormHeaderDetailActions from '../../../components/action_button/FormHeaderDetailActions'
import GoogleIcon from '../../../components/icon/GoogleIcon'
import {
  UI_COLOR_TEXT_BODY_DARK,
  UI_COLOR_TEXT_SUMMARY_LABEL
} from '../../../constants/uiColors'
import { UI_TEXT_EMPTY_VALUE } from '../../../constants/uiText'
import { RbacStatusChip } from '../../components/RbacChips'
import {
  RBAC_DETAIL_ICON_COLOR,
  rbacDetailsCardGridSx,
  rbacDetailsFieldSx,
  rbacSummaryCardSx,
  rbacSummaryMetaItemSx,
  rbacSummaryMetaTextSx,
  rbacSummaryTitleSx
} from '../../components/constants/rbacDetailCardConstants'
import RbacRoleSectionCard from '../components/RbacRoleSectionCard'
import { RBAC_ROLE_DETAIL_TEXTS } from '../shared/rbacRoleDetailConstants'
import {
  formatRbacRoleCreatedBy,
  formatRbacRoleDate,
  formatRbacRoleUsersCount,
  getRbacRoleStatusMeta
} from '../shared/rbacRoleDetailHelpers'

const detailsFieldLabelSx = {
  color: UI_COLOR_TEXT_SUMMARY_LABEL,
  fontSize: 13,
  fontWeight: 800,
  lineHeight: 1.2,
  textTransform: 'uppercase'
}

const detailsFieldValueSx = {
  color: UI_COLOR_TEXT_BODY_DARK,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.4,
  mt: 0.5,
  wordBreak: 'break-word'
}

function SummaryMetaItem({ icon, label, value }) {
  return (
    <Box sx={rbacSummaryMetaItemSx}>
      <GoogleIcon name={icon} size={14} color={RBAC_DETAIL_ICON_COLOR} />
      <Typography component="span" sx={{ ...rbacSummaryMetaTextSx, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography component="span" sx={{ ...rbacSummaryMetaTextSx, fontWeight: 400 }}>
        {value}
      </Typography>
    </Box>
  )
}

function RbacRoleSummaryCard({ role }) {
  const statusMeta = getRbacRoleStatusMeta(role)

  return (
    <Box sx={rbacSummaryCardSx}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography
          fontWeight={700}
          fontSize={16}
          sx={rbacSummaryTitleSx}
        >
          {role?.name || '—'}
        </Typography>
        <RbacStatusChip label={statusMeta.label} />
      </Stack>

      <Stack direction="row" spacing={2.5} useFlexGap flexWrap="wrap" alignItems="center">
        <SummaryMetaItem icon="badge" label="Código:" value={role?.uid || '—'} />
        <SummaryMetaItem
          icon="event"
          label="Fecha de creación:"
          value={formatRbacRoleDate(role?.created_at)}
        />
      </Stack>
    </Box>
  )
}

function DetailFieldMui({ label, value, fullWidth = false }) {
  return (
    <Box sx={{ ...rbacDetailsFieldSx, gridColumn: fullWidth ? { md: '1 / -1' } : undefined }}>
      <Typography
        sx={{
          color: UI_COLOR_TEXT_SUMMARY_LABEL,
          fontSize: 13,
          fontWeight: 800,
          lineHeight: 1.2,
          textTransform: 'uppercase'
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{
          color: UI_COLOR_TEXT_BODY_DARK,
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.4,
          mt: 0.5,
          wordBreak: 'break-word'
        }}
      >
        {value || UI_TEXT_EMPTY_VALUE}
      </Typography>
    </Box>
  )
}

function DetailsSummarySectionMui({ summaryTitle, summaryFields = [] }) {
  const visibleFields = summaryFields.filter((field) => !field.hidden)

  if (!visibleFields.length) return null

  return (
    <RbacRoleSectionCard title={summaryTitle} iconName="info" bodySx={rbacDetailsCardGridSx}>
      {visibleFields.map((field, index) => (
        <DetailFieldMui
          key={`${field.label}-${index}`}
          label={field.label}
          value={field.value}
          fullWidth={field.fullWidth}
        />
      ))}
    </RbacRoleSectionCard>
  )
}

export default function RbacRoleDetailTab({ role, canManage, onEditRole, onStatusAction }) {
  const statusMeta = getRbacRoleStatusMeta(role)
  const fields = [
    { label: 'Nombre del rol', value: role?.name },
    { label: 'Usuarios asignados', value: formatRbacRoleUsersCount(role) },
    { label: 'Creado por', value: formatRbacRoleCreatedBy(role) },
    { label: 'Estado', value: <RbacStatusChip label={statusMeta.label} /> },
    { label: 'Descripción', value: role?.description, fullWidth: true }
  ]

  return (
    <div className="container-fluid px-0">
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mt: 3,
          mb: 2
        }}
      >
        <Typography sx={{ fontSize: 16, m: 0, lineHeight: 1.3 }}>
          {RBAC_ROLE_DETAIL_TEXTS.detailTitle}
        </Typography>

        {canManage && (
          <FormHeaderDetailActions
            secondaryLabel={
              statusMeta.isActive
                ? RBAC_ROLE_DETAIL_TEXTS.inactivateRole
                : RBAC_ROLE_DETAIL_TEXTS.activateRole
            }
            secondaryIcon={statusMeta.isActive ? Prohibit : Power}
            secondaryVariant={statusMeta.isActive ? 'danger' : 'success'}
            onSecondary={onStatusAction}
            primaryLabel={RBAC_ROLE_DETAIL_TEXTS.editRole}
            primaryIcon={PencilSimpleLine}
            onPrimary={onEditRole}
            stackOnMobile
          />
        )}
      </Box>

      <RbacRoleSummaryCard role={role} />

      <DetailsSummarySectionMui
        summaryTitle={RBAC_ROLE_DETAIL_TEXTS.summaryTitle}
        summaryFields={fields}
      />
    </div>
  )
}
